'use client';

import type { UpsertJobOfferInputDTO } from '@algoacademy/backend/modules/career/endpoints/mutations/upsert-job-offer/input.dto';
import { Button } from '@algoacademy/design-system/web/actions/Button';
import { LightCard } from '@algoacademy/design-system/web/data-display/Card';
import { showTrpcMutationErrorToast } from '@algoacademy/design-system/web/feedback/Toaster/ToasterError';
import { ErrorMessage, Field } from '@algoacademy/design-system/web/forms/Fieldset';
import { Input } from '@algoacademy/design-system/web/forms/Input';
import { FullscreenMarkdownEditor } from '@algoacademy/design-system/web/forms/MarkdownEditor';
import { Textarea } from '@algoacademy/design-system/web/forms/Textarea';
import { Divider } from '@algoacademy/design-system/web/layouts/Divider';
import { H1, H3 } from '@algoacademy/design-system/web/typography/Heading';
import { P2 } from '@algoacademy/design-system/web/typography/Paragraph';
import { useMutation } from '@tanstack/react-query';
import { useRouter, useSearchParams } from 'next/navigation';
import { useNavigationGuard } from 'next-navigation-guard';
import { FormProvider, useForm } from 'react-hook-form';
import { useTRPC } from '@/app/_trpc/config';
import type { JobOfferDTO } from '../../page';
import { EmploymentContract } from './_components/EmploymentContract';
import { ManagePublishingButtons } from './_components/ManagePublishingButtons';
import { RecruitmentProcessSection } from './_components/RecruitmentProcess';
import { UnsavedChangesDialog } from './_components/UnsavedChangesDialog';
import { getUpsertJobOfferByIdFormDefaultValues } from './values';
export function UpsertJobOfferByIdForm({
  jobOffer
}: {
  jobOffer?: JobOfferDTO;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const mode = searchParams.get('mode');
  const jobOfferId = jobOffer?.id;
  const trpc = useTRPC();
  const upsertJobOfferByIdMutation = useMutation(trpc.career.upsertJobOffer.mutationOptions({}));
  const methods = useForm<UpsertJobOfferInputDTO>(getUpsertJobOfferByIdFormDefaultValues(jobOffer));
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isDirty
    }
  } = methods;
  const onSubmit = async (data: UpsertJobOfferInputDTO) => {
    try {
      await upsertJobOfferByIdMutation.mutateAsync({
        id: jobOfferId,
        positionName: data.positionName,
        description: data.description,
        isActive: data.isActive,
        recruitmentProcess: data.recruitmentProcess,
        markdownAndAttachments: {
          markdown: {
            id: jobOffer?.markdownAndAttachments.markdown.id ?? '',
            content: data.markdownAndAttachments.markdown.content
          },
          attachments: data.markdownAndAttachments.attachments?.map(attachment => ({
            id: attachment?.id,
            name: attachment?.name,
            url: attachment?.url
          })) ?? []
        },
        salary: data.salary
      });
    } catch (error: unknown) {
      showTrpcMutationErrorToast(error);
      return;
    }
    router.push('/kariera');
  };
  const navigationGuard = useNavigationGuard({
    enabled: isDirty
  });

  // if (!user.hasPermissions([PERMISSIONS.JOB_OFFERS.MANAGE.ENABLED])) {
  //   redirect(`/kariera/${btoa(jobOfferId)}`)
  // }

  return <main className='mx-auto w-full max-w-screen-7xl space-y-6'>
      <div className='flex items-center justify-between'>
        <H1>{mode === 'create' ? 'Dodaj ofertę' : 'Edytuj ofertę'}</H1>

        <ManagePublishingButtons jobOfferIsActive={jobOffer?.isActive} jobOfferId={jobOfferId} />
      </div>

      <Divider />

      <UnsavedChangesDialog navigationGuard={navigationGuard} />

      <FormProvider {...methods}>
        <form className='space-y-6' onSubmit={handleSubmit(onSubmit)}>
          <LightCard className='space-y-4 p-6'>
            <H3>Nazwa stanowiska</H3>
            <Field>
              <Input {...register('positionName')} placeholder='Nazwa stanowiska' />
              <ErrorMessage>{errors.positionName?.message}</ErrorMessage>
            </Field>
          </LightCard>

          <LightCard className='space-y-4 p-6'>
            <H3>Krótki opis</H3>
            <P2 className='text-contentSecondary'>
              Dodaj przykładowe umiejętności powiązane z ofertą.
            </P2>
            <Field>
              <Textarea {...register('description')} placeholder='Krótki opis' rows={4} />
              <ErrorMessage>{errors.description?.message}</ErrorMessage>
            </Field>
          </LightCard>

          <EmploymentContract jobOffer={jobOffer} />

          <LightCard className='space-y-4 p-6'>
            <H3>Treść oferty</H3>
            <Field>
              {/* todo: delete markdownOnly if attachments are needed here */}
              <FullscreenMarkdownEditor markdownOnly // todo: delete if want attachments here
            height={40} value={{
              markdown: jobOffer?.markdownAndAttachments.markdown.content ?? ''
            }} onChange={content => {
              methods.setValue('markdownAndAttachments.markdown.content', content.markdown);
            }} />
            </Field>
          </LightCard>

          <RecruitmentProcessSection />

          <div className='flex justify-end gap-3'>
            <Button outline href='/kariera'>
              Anuluj
            </Button>

            <Button type='submit'>Zapisz zmiany</Button>
          </div>
        </form>
      </FormProvider>
    </main>;
}